// src/pages/HousekeeperDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import { Alert, Button, Drawer, Form, Modal, Progress, Select, Spin, Table } from "antd";
import { FiCheckCircle, FiCircle } from "react-icons/fi";
import {
  CardTitle,
  DetailList,
  ListToolbar,
  PageHeader,
  RowActions,
  Section,
  SectionCard,
  StatusTag,
  TabBar,
  fmtDateTime,
  tablePagination,
  useFlatPage,
} from "../components/ui";
import { IconPlus } from "../components/ui/icons";

import housekeepingApi from "../api/housekeepingApi";
import notify from "../utils/notify";

const { Option } = Select;

const HOUSEKEEPING_TASK_STATUS = {
  Pending: "Pending",
  InProgress: "InProgress",
  Completed: "Completed",
  Cancelled: "Cancelled",
};

const statusLabel = (s) => (s === "InProgress" ? "In progress" : s || "Unknown");
const statusTag = (status) => <StatusTag status={status}>{statusLabel(status)}</StatusTag>;

const taskTitle = (t) =>
  (t?.title && t.title.trim()) ||
  `${String(t?.taskType || "Cleaning")
    .replace(/([A-Z])/g, " $1")
    .trim()} — Room ${t?.roomNumber ?? t?.roomId ?? "N/A"}`;

const calcChecklistProgress = (task) => {
  const items = Array.isArray(task?.checklistItems) ? task.checklistItems : [];
  const total = items.length;
  const done = items.filter((x) => !!x?.isCompleted).length;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  return { total, done, percent };
};

const HousekeeperDashboard = () => {
  useFlatPage();
  const { authData } = useAuth();
  const navigate = useNavigate();

  const hotelId = authData?.hotelId;

  // data
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // assign
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignTaskModalVisible, setAssignTaskModalVisible] = useState(false);
  const [assignTaskForm] = Form.useForm();

  // details (read-only)
  const [currentTask, setCurrentTask] = useState(null);
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [taskDetailsLoading, setTaskDetailsLoading] = useState(false);

  // filters
  const [filters, setFilters] = useState({ status: null, search: "" });

  const fetchAssignableUsers = useCallback(async () => {
    if (!hotelId) return;

    setLoadingUsers(true);
    try {
      const response = await housekeepingApi.getAssignableUsers(hotelId);
      setAssignableUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // Soft warning for 404
      if (error?.response?.status === 404) {
        notify.info({
          title: "No Staff Available",
          description:
            "No users with housekeeping permissions found. You can still view tasks, but assignment will be unavailable until permissions are configured.",
          duration: 4,
          placement: "topRight",
        });
      } else {
        notify.error({
          title: "Error Loading Staff",
          description: error?.response?.data?.message || "Failed to load assignable users",
          duration: 4,
        });
      }
      setAssignableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [hotelId]);

  const fetchTasks = useCallback(async () => {
    if (!hotelId) {
      setTasks([]);
      setFilteredTasks([]);
      setLoading(false);
      notify.error({
        title: "Hotel context missing",
        description: "Please log in again.",
      });
      return;
    }

    setLoading(true);
    try {
      // Use ALL tasks for assignment + monitoring.
      const response = await housekeepingApi.getAllTasks(hotelId);
      const fetched = Array.isArray(response?.data) ? response.data : [];
      setTasks(fetched);
      setFilteredTasks(fetched);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      notify.error({
        title: "Error",
        description: error?.response?.data?.message || "Failed to load tasks",
      });
      setTasks([]);
      setFilteredTasks([]);
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  useEffect(() => {
    (async () => {
      await Promise.all([fetchTasks(), fetchAssignableUsers()]);
    })();
  }, [fetchTasks, fetchAssignableUsers]);

  const stats = useMemo(() => {
    const s = { pending: 0, inProgress: 0, completed: 0, cancelled: 0 };
    for (const t of tasks) {
      if (t.status === HOUSEKEEPING_TASK_STATUS.Pending) s.pending++;
      else if (t.status === HOUSEKEEPING_TASK_STATUS.InProgress) s.inProgress++;
      else if (t.status === HOUSEKEEPING_TASK_STATUS.Completed) s.completed++;
      else if (t.status === HOUSEKEEPING_TASK_STATUS.Cancelled) s.cancelled++;
    }
    return s;
  }, [tasks]);

  useEffect(() => {
    const q = (filters.search || "").trim().toLowerCase();
    const st = filters.status;

    const next = tasks.filter((t) => {
      const statusOk = !st || t.status === st;

      const text = `${taskTitle(t)} ${t.description || ""} ${t.roomNumber || ""}`.toLowerCase();
      const searchOk = !q || text.includes(q);

      return statusOk && searchOk;
    });

    setFilteredTasks(next);
  }, [tasks, filters]);

  const openTaskDetails = async (task) => {
    const id = task?.housekeepingTaskId ?? task?.id;
    if (!id) return;

    setInfoModalVisible(true);
    setTaskDetailsLoading(true);
    setCurrentTask(null);

    try {
      // IMPORTANT: your controller GET requires hotelId query:
      // GET api/Housekeeping/{id}?hotelId=123
      const res = await housekeepingApi.getTaskById(id, hotelId);
      setCurrentTask(res?.data || task);
    } catch (e) {
      // fallback to row data, but checklistItems may be missing
      console.error("Failed to load task details:", e);
      setCurrentTask(task);
      notify.warning({
        title: "Partial details",
        description:
          e?.response?.data?.message ||
          "Could not load full task details. Showing what is available from the list.",
      });
    } finally {
      setTaskDetailsLoading(false);
    }
  };

  const openAssignModal = () => {
    assignTaskForm.resetFields();
    setAssignTaskModalVisible(true);
  };

  const handleAssignTaskSubmit = async () => {
    try {
      const v = await assignTaskForm.validateFields();

      await housekeepingApi.assignTask({
        taskId: v.taskId,
        userId: v.userId,
        hotelId,
      });

      notify.success({
        title: "Assigned",
        description: `Task #${v.taskId} assigned successfully.`,
      });

      setAssignTaskModalVisible(false);
      await fetchTasks();
    } catch (error) {
      if (error?.errorFields) return; // form validation
      notify.error({
        title: "Assignment failed",
        description: error?.response?.data?.message || error?.message || "Failed to assign task",
      });
    }
  };

  const tabs = useMemo(
    () => [
      { key: "all", label: "All", count: tasks.length },
      { key: HOUSEKEEPING_TASK_STATUS.Pending, label: "Pending", count: stats.pending },
      { key: HOUSEKEEPING_TASK_STATUS.InProgress, label: "In progress", count: stats.inProgress },
      { key: HOUSEKEEPING_TASK_STATUS.Completed, label: "Completed", count: stats.completed },
      { key: HOUSEKEEPING_TASK_STATUS.Cancelled, label: "Cancelled", count: stats.cancelled },
    ],
    [tasks.length, stats]
  );

  const columns = [
    {
      title: "Task",
      key: "title",
      render: (_, record) => (
        <div className="hms-cell-stack">
          <span className="hms-cell-main">{taskTitle(record)}</span>
          <span className="hms-cell-sub">#{record.housekeepingTaskId}</span>
        </div>
      ),
    },
    {
      title: "Room",
      dataIndex: "roomNumber",
      key: "roomNumber",
      width: 110,
      render: (text) => text || "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: statusTag,
    },
    {
      title: "Progress",
      key: "progress",
      width: 220,
      render: (_, record) => {
        // The list endpoint may not include checklistItems; the details drawer loads the full task.
        if (!record.checklistItems) return <span className="hms-muted">Open details</span>;
        const { done, total, percent } = calcChecklistProgress(record);
        return (
          <div className="hms-cell-stack" style={{ minWidth: 160 }}>
            <Progress percent={percent} size="small" showInfo={false} />
            <span className="hms-cell-sub">
              {done} of {total} done
            </span>
          </div>
        );
      },
    },
    {
      title: "Assigned",
      dataIndex: "assignedAt",
      key: "assignedAt",
      width: 190,
      sorter: (a, b) => new Date(a.assignedAt || 0) - new Date(b.assignedAt || 0),
      render: (date) => (date ? fmtDateTime(date) : <span className="hms-muted">—</span>),
    },
    {
      title: "",
      key: "actions",
      fixed: "right",
      align: "right",
      width: 110,
      render: (_, record) => (
        <RowActions
          actions={[
            { key: "view", label: "View details", icon: "view", onClick: () => openTaskDetails(record) },
            {
              key: "assign",
              label: "Assign task",
              icon: "assign",
              hidden: record.status !== HOUSEKEEPING_TASK_STATUS.Pending,
              keepSpace: true,
              disabled: assignableUsers.length === 0,
              disabledReason: "No assignable users available",
              onClick: () => {
                openAssignModal();
                assignTaskForm.setFieldsValue({ taskId: record.housekeepingTaskId });
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="hms-page hk-page">
      <PageHeader
        actions={
          <>
            <Button onClick={() => navigate("/hotel/housekeeping/checklists")}>Checklists</Button>
            <Button
              type="primary"
              icon={<IconPlus />}
              onClick={openAssignModal}
              disabled={assignableUsers.length === 0 || stats.pending === 0}
            >
              Assign task
            </Button>
          </>
        }
      />

      {assignableUsers.length === 0 && !loadingUsers && (
        <Alert
          style={{ marginBottom: 12 }}
          message="No staff available for assignment"
          description="No users with housekeeping permissions were found. Configure permissions to enable assigning tasks."
          type="warning"
          showIcon
          action={
            <Button type="primary" onClick={() => navigate("/hotels/PermissionManager")}>
              Configure permissions
            </Button>
          }
        />
      )}

      <SectionCard
        flush
        title={<CardTitle count={tasks.length}>Assignments and progress</CardTitle>}
        extra={
          <ListToolbar
            search={{
              value: filters.search,
              onChange: (v) => setFilters({ ...filters, search: v }),
              placeholder: "Search tasks",
            }}
            onRefresh={fetchTasks}
            loading={loading}
          />
        }
      >
        <TabBar tabs={tabs} value={filters.status || "all"} onChange={(k) => setFilters({ ...filters, status: k === "all" ? null : k })} />

        <Table
          className="hms-table"
          rowKey="housekeepingTaskId"
          columns={columns}
          dataSource={filteredTasks}
          loading={loading}
          pagination={tablePagination("task")}
          scroll={{ x: 900 }}
          onRow={(record) => ({ onClick: () => openTaskDetails(record), style: { cursor: "pointer" } })}
          locale={{ emptyText: <div className="hms-empty">No tasks in this view</div> }}
        />
      </SectionCard>

      <Modal
        title="Assign task"
        open={assignTaskModalVisible}
        onCancel={() => setAssignTaskModalVisible(false)}
        onOk={handleAssignTaskSubmit}
        okText="Assign"
        confirmLoading={loadingUsers}
        width={520}
        destroyOnHidden
      >
        {assignableUsers.length === 0 ? (
          <Alert
            message="No staff available"
            description="There are no users with housekeeping permissions. Please configure user permissions first."
            type="warning"
            showIcon
          />
        ) : (
          <Form form={assignTaskForm} layout="vertical">
            <Form.Item name="taskId" label="Task" rules={[{ required: true, message: "Please select a task" }]}>
              <Select placeholder="Select a task to assign" loading={loading}>
                {tasks
                  .filter((t) => t.status === HOUSEKEEPING_TASK_STATUS.Pending)
                  .map((t) => (
                    <Option key={t.housekeepingTaskId} value={t.housekeepingTaskId}>
                      {taskTitle(t)} (Room {t.roomNumber || "—"})
                    </Option>
                  ))}
              </Select>
            </Form.Item>

            <Form.Item name="userId" label="Assign to" rules={[{ required: true, message: "Please select a user" }]}>
              <Select placeholder="Select staff member">
                {assignableUsers.map((u) => (
                  <Option key={u.userId} value={u.userId}>
                    {u.firstName} {u.lastName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Drawer
        title="Task details"
        open={infoModalVisible}
        onClose={() => setInfoModalVisible(false)}
        size={560}
        destroyOnHidden
      >
        {taskDetailsLoading ? (
          <div style={{ padding: 36, textAlign: "center" }}>
            <Spin />
          </div>
        ) : currentTask ? (
          (() => {
            const { done, total, percent } = calcChecklistProgress(currentTask);
            const items = Array.isArray(currentTask.checklistItems) ? currentTask.checklistItems : [];
            return (
              <>
                <div className="hms-summary">
                  <div>
                    <div className="hms-summary-title">{taskTitle(currentTask)}</div>
                    <div className="hms-summary-sub">Task #{currentTask.housekeepingTaskId}</div>
                  </div>
                  {statusTag(currentTask.status)}
                </div>

                <DetailList
                  items={[
                    { label: "Room", value: currentTask.roomNumber },
                    { label: "Description", value: currentTask.description },
                    { label: "Assigned", value: currentTask.assignedAt ? fmtDateTime(currentTask.assignedAt) : null },
                    { label: "Started", value: currentTask.startedAt ? fmtDateTime(currentTask.startedAt) : null },
                    { label: "Completed", value: currentTask.completedAt ? fmtDateTime(currentTask.completedAt) : null },
                    { label: "Notes", value: currentTask.notes },
                  ]}
                />

                <Section title="Checklist" extra={<span className="hms-pill">{done} of {total}</span>}>
                  <Progress percent={percent} />
                  {items.length === 0 ? (
                    <div className="hms-empty">No checklist items for this task</div>
                  ) : (
                    <ul className="hms-checklist">
                      {items.map((item, idx) => (
                        <li key={item.checklistItemId ?? item.id ?? idx} className={item.isCompleted ? "is-done-row" : ""}>
                          <span className="hms-check">
                            {item.isCompleted ? <FiCheckCircle /> : <FiCircle />}
                            <span className={item.isCompleted ? "is-done" : ""}>{item.description}</span>
                          </span>
                          {item.isCompleted && item.completedAt ? (
                            <span className="hms-cell-sub">{fmtDateTime(item.completedAt)}</span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>
              </>
            );
          })()
        ) : (
          <Alert type="warning" message="No task loaded" showIcon />
        )}
      </Drawer>
    </div>
  );
};

export default HousekeeperDashboard;
